import { type OptionItem } from "@common/declarations";
import { Common } from "@common/types";
import * as React from "react";

export interface WithDynamicOptionsAddedProps<TOption> {
  dependent?: any;
  fetchOptions: () => Promise<TOption[]>;
  name?: string;
  onLoaded?: (options: TOption[]) => void;
}
type State<TOption> = Common.WithOptions<TOption> & {
  isLoading: boolean;
};

export type WithDynamicOptionsProps<OriginalProps, TOption = OptionItem<any>> = Omit<
  OriginalProps,
  "allOptions" | "options"
> &
  WithDynamicOptionsAddedProps<TOption>;

const withDynamicOptionsHOC =
  <TOption, OriginalProps extends Common.WithOptionsFn<TOption> & { isLoading?: boolean }>(
    Cmp: React.ComponentType<OriginalProps>
  ): React.FunctionComponent<WithDynamicOptionsProps<OriginalProps, TOption>> =>
  (props: OriginalProps & WithDynamicOptionsAddedProps<TOption>) => {
    const [state, setState] = React.useState<State<TOption>>({
      isLoading: true,
      options: []
    });

    React.useEffect(() => {
      let isCancelled = false;

      const init = async () => {
        setState({ options: [], isLoading: true });
        try {
          const options = await props.fetchOptions();
          if (isCancelled) return;

          setState({ isLoading: false, options });
        } catch (e) {
          console.error(e);
        } finally {
          !isCancelled && setState((x) => ({ isLoading: false, options: x.options }));
        }
      };

      init();

      return () => {
        isCancelled = true;
      };
    }, [props.dependent]);

    React.useEffect(() => {
      !state.isLoading && props.onLoaded && props.onLoaded(state.options);
    }, [state.isLoading, state.options]); // including props.onLoaded into deps may lead to infinite loop

    return <Cmp {...props} {...state} />;
  };
export default withDynamicOptionsHOC;
